import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../../../API';
import styles from './RenameResourceModal.module.css';

/**
 * Modal to rename a GirderProjects folder or project item.
 *
 * Folder: pass folderId → PUT projects/folders/:id/rename
 * Item: pass projectFolderId + itemId → PUT projects/:projectFolderId/items/:itemId/rename
 *
 * @param {{
 *   open: boolean,
 *   folderId?: string | null,
 *   projectFolderId?: string | null,
 *   itemId?: string | null,
 *   initialName: string,
 *   title?: string,
 *   onClose: () => void,
 *   onSuccess: (updatedDoc: object) => void,
 * }} props
 */
export default function RenameResourceModal({
    open,
    folderId = null,
    projectFolderId = null,
    itemId = null,
    initialName,
    title,
    onClose,
    onSuccess,
}) {
    const isItem = Boolean(itemId && projectFolderId);
    const dialogTitle = title || (isItem ? 'Rename item' : 'Rename folder');
    const resourceId = isItem ? itemId : folderId;

    const [name, setName] = useState('');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setName(initialName ?? '');
            setError(null);
            const t = requestAnimationFrame(() => {
                inputRef.current?.focus();
                inputRef.current?.select?.();
            });
            return () => cancelAnimationFrame(t);
        }
        return undefined;
    }, [open, initialName]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open || !resourceId) {
        return null;
    }

    async function submit() {
        const trimmed = name.trim();
        if (!trimmed) {
            setError('Name cannot be empty.');
            return;
        }
        setSubmitting(true);
        setError(null);
        const path = isItem
            ? `projects/${projectFolderId}/items/${itemId}/rename`
            : `projects/folders/${folderId}/rename`;
        try {
            const r = await api.put(path, { json: { name: trimmed } });
            if (r.status === 200) {
                onSuccess(r.data);
                onClose();
            } else {
                const msg =
                    typeof r.data === 'object' && r.data?.message
                        ? r.data.message
                        : 'Rename failed.';
                setError(msg);
            }
        } catch (e) {
            setError(e?.message || 'Rename failed.');
        } finally {
            setSubmitting(false);
        }
    }

    return createPortal(
        <div className={styles.scrim} role="presentation" onClick={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="rename-resource-title"
                className={styles.panel}
                onClick={(e) => e.stopPropagation()}
            >
                <h2 id="rename-resource-title" className={styles.title}>
                    {dialogTitle}
                </h2>
                <label className={styles.label} htmlFor="rename-resource-input">
                    Name
                </label>
                <input
                    ref={inputRef}
                    id="rename-resource-input"
                    className={styles.input}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            void submit();
                        }
                    }}
                />
                {error ? <p className={styles.error}>{error}</p> : null}
                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.btnSecondary}
                        onClick={onClose}
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.btnPrimary}
                        onClick={() => void submit()}
                        disabled={submitting}
                    >
                        {submitting ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
